import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@/ui";
import { useNavigate } from "react-router-dom";
import { useAlert } from "@/app/providers/AlertProvider";
import Header from "@/components/Header";
import BottomTabBar from "@/components/BottomTabBar";
import { DownloadIcon, EmailIcon, ExpandMoreIcon } from "@/ui";
import { resolveCustomerId } from "@/shared/lib/customerIdResolver";
import { BudgetService, BenefitsService } from "@/shared/api/services";
import { httpGet } from "@/shared/api/http";
import { customerEndpoints } from "@/shared/api/endpoints";
import type { ApiResponse } from "@/shared/api/types";

interface BudgetSummaryData {
  calculatedMonthlyIncome: number;
  calculatedAssets: number;
  calculatedFixedMonthlyExpenses: number;
  calculatedFlexibleMonthlyExpenses: number;
  pendingPaymentOffers: number;
  totalPriorityDebts: number;
  monthlyPriorityDebtRepayment: number;
  totalNonPriorityDebts: number;
  monthlyNonPriorityDebtRepayment: number;
  totalLeftover: number;
  estimatedBenefits: number | undefined;
}

interface CalculatedTotalsRecord {
  customerId: number;
  totalIncome: number;
  totalAssets: number;
  totalOutgoingsFixed: number;
  totalOutgoingsFlexible: number;
  totalOwingOnPriorityDebt: number;
  totalRepayOnPriorityDebt: number;
  totalOwingOnNonPriorityDebt: number;
  totalRepayOnNonPriorityDebt: number;
  pendingPaymentOffer: number;
  balanceAmount: number;
  totalRepaymentDebts: number;
  totalOwingOnDebts: number;
}

const MonthlyBudgetSummary: React.FC = () => {
  const theme = useTheme();
  useMediaQuery(theme.breakpoints.down("md")); // Used for responsive design
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [headerHeight, setHeaderHeight] = useState<number>(56);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [budgetData, setBudgetData] = useState<BudgetSummaryData>({
    calculatedMonthlyIncome: 0,
    calculatedAssets: 0,
    calculatedFixedMonthlyExpenses: 0,
    calculatedFlexibleMonthlyExpenses: 0,
    pendingPaymentOffers: 0,
    totalPriorityDebts: 0,
    monthlyPriorityDebtRepayment: 0,
    totalNonPriorityDebts: 0,
    monthlyNonPriorityDebtRepayment: 0,
    totalLeftover: 0,
    estimatedBenefits: undefined, // Will be fetched from API
  });

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | undefined>(
    undefined
  );
  const [isEmailSending, setIsEmailSending] = useState(false);

  // Fetch budget summary data
  useEffect(() => {
    const fetchBudgetSummary = async () => {
      try {
        setIsSummaryLoading(true);
        const customerId = await resolveCustomerId();
        if (!customerId) throw new Error("Customer ID not found");

        // Fetch calculated totals from backend (single source of truth for budget summary)
        const totalsResponse = await httpGet<
          ApiResponse<CalculatedTotalsRecord[]>
        >(customerEndpoints.getCalculatedTotals(customerId));

        // API may return either { data: [...] } or [...]
        const rawTotals = (totalsResponse.data?.data ?? totalsResponse.data) as
          | CalculatedTotalsRecord[]
          | CalculatedTotalsRecord
          | null;

        const totals: CalculatedTotalsRecord | null = Array.isArray(rawTotals)
          ? (rawTotals[0] ?? null)
          : rawTotals;

        const toNumber = (value: unknown): number => {
          const n =
            typeof value === "string"
              ? parseFloat(value)
              : typeof value === "number"
                ? value
                : Number(value);
          return Number.isFinite(n) ? n : 0;
        };

        const calculatedMonthlyIncome = totals
          ? toNumber(totals.totalIncome)
          : 0;
        const calculatedAssets = totals ? toNumber(totals.totalAssets) : 0;
        const calculatedFixedMonthlyExpenses = totals
          ? toNumber(totals.totalOutgoingsFixed)
          : 0;
        const calculatedFlexibleMonthlyExpenses = totals
          ? toNumber(totals.totalOutgoingsFlexible)
          : 0;
        const pendingPaymentOffers = totals
          ? toNumber(totals.pendingPaymentOffer)
          : 0;
        const totalPriorityDebts = totals
          ? toNumber(totals.totalOwingOnPriorityDebt)
          : 0;
        const monthlyPriorityDebtRepayment = totals
          ? toNumber(totals.totalRepayOnPriorityDebt)
          : 0;
        const totalNonPriorityDebts = totals
          ? toNumber(totals.totalOwingOnNonPriorityDebt)
          : 0;
        const monthlyNonPriorityDebtRepayment = totals
          ? toNumber(totals.totalRepayOnNonPriorityDebt)
          : 0;
        const totalLeftover = totals ? toNumber(totals.balanceAmount) : 0;

        // Fetch benefits estimation (non-blocking)
        let estimatedBenefits: number | undefined = undefined;
        try {
          const benefitsResp = await BenefitsService.startCheck(customerId, {
            skipErrorCapture: true,
          });
          if (benefitsResp?.success && benefitsResp.data) {
            const benefitsAmount =
              benefitsResp.data.extraMonthlyBenefitsAmount || 0;
            if (benefitsAmount) {
              estimatedBenefits =
                typeof benefitsAmount === "string"
                  ? parseFloat(benefitsAmount)
                  : benefitsAmount;
            }
          }
        } catch (error) {
          console.warn(
            "Failed to fetch benefits estimation (non-critical):",
            error
          );
          // Non-blocking - continue without benefits amount
        }

        setBudgetData({
          calculatedMonthlyIncome,
          calculatedAssets,
          calculatedFixedMonthlyExpenses,
          calculatedFlexibleMonthlyExpenses,
          pendingPaymentOffers,
          totalPriorityDebts,
          monthlyPriorityDebtRepayment,
          totalNonPriorityDebts,
          monthlyNonPriorityDebtRepayment,
          totalLeftover,
          estimatedBenefits,
        });
      } catch (error) {
        console.error("Failed to fetch budget summary:", error);
        showAlert({
          severity: "error",
          message: "Failed to load budget summary data.",
          autoHide: true,
          duration: 5000,
        });
      } finally {
        setIsSummaryLoading(false);
      }
    };

    fetchBudgetSummary();
  }, [showAlert]);

  const handleDownload = () => {
    (async () => {
      try {
        const customerId = await resolveCustomerId();
        if (!customerId) throw new Error("Customer ID not found");

        await BudgetService.downloadBudgetPdf(customerId);

        showAlert({
          severity: "success",
          message: "Budget PDF downloaded",
          autoHide: true,
          duration: 2500,
        });
      } catch (error) {
        console.error("Download failed:", error);
        showAlert({
          severity: "error",
          message: "Failed to download budget PDF",
          autoHide: true,
          duration: 4000,
        });
      }
    })();
  };

  const handleEmail = () => {
    // Resolve recipient email from localStorage (legacy parity)
    const rawUser =
      typeof window !== "undefined" ? localStorage.getItem("userData") : null;
    const userEmail = rawUser
      ? (() => {
          try {
            return JSON.parse(rawUser).emailAddress as string | undefined;
          } catch {
            return undefined;
          }
        })()
      : undefined;

    if (!userEmail) {
      showAlert({
        severity: "warning",
        message: "Your email address is missing. Please update your profile.",
        autoHide: true,
        duration: 4000,
      });
      return;
    }

    // Open confirmation dialog
    setPendingEmail(userEmail);
    setEmailDialogOpen(true);
  };

  const handleEmailConfirm = async () => {
    if (!pendingEmail || isEmailSending) return;

    setIsEmailSending(true);

    try {
      const customerId = await resolveCustomerId();
      if (!customerId) throw new Error("Customer ID not found");

      await BudgetService.emailBudget(customerId);

      setEmailDialogOpen(false);
      showAlert({
        severity: "success",
        message: "Email sent successfully",
        autoHide: true,
        duration: 3000,
      });
    } catch (error) {
      console.error("Email failed:", error);
      showAlert({
        severity: "error",
        message: "Failed to send email",
        autoHide: true,
        duration: 4000,
      });
    } finally {
      setIsEmailSending(false);
      setPendingEmail(undefined);
    }
  };

  const handleEmailCancel = () => {
    if (isEmailSending) return; // Prevent closing while sending
    setEmailDialogOpen(false);
    setPendingEmail(undefined);
  };

  const handleBenefitsCheck = () => {
    navigate("/benefits");
  };

  const handleViewBreakdown = () => {
    navigate("/budget");
  };

  const formatCurrency = (value: number) => {
    return `£${value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const SummaryRow: React.FC<{
    label: string;
    value: number;
    bold?: boolean;
  }> = ({ label, value, bold = false }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.75,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: "14px",
          color: "#616161",
          fontWeight: 400,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          color: "#003c42",
          fontWeight: bold ? 700 : 600,
        }}
      >
        {formatCurrency(value)}
      </Typography>
    </Box>
  );

  return (
    <>
      {isSummaryLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            backgroundColor: "rgba(249, 249, 249, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: "#155f54" }} />
            <Typography
              variant="body2"
              sx={{ fontSize: "14px", color: "#616161" }}
            >
              Loading your budget summary...
            </Typography>
          </Box>
        </Box>
      )}

      {/* Mobile View */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          height: "100dvh",
          width: "100vw",
          backgroundColor: "#f9f9f9",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Header onHeightChange={setHeaderHeight} />

        {/* Content with padding for footer */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            backgroundColor: "#f9f9f9",
            px: 2,
            pt: `calc(${headerHeight}px + 16px)`,
            pb: "calc(80px + env(safe-area-inset-bottom))", // Space for BottomTabBar (+ breathing room)
            overflow: "auto",
            position: "relative",
          }}
        >
          {/* Page Title */}
          <Typography
            variant="h4"
            sx={{
              fontSize: "24px",
              fontWeight: 700,
              color: "#003c42",
              mb: 2,
            }}
          >
            Your monthly budget: Summary
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              color: "#757575",
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            Below is a summary of the information that you have provided us.
            This is your budget. Please check all the details are correct, then
            click continue.
          </Typography>

          {/* Benefits Check Card */}
          <Box
            sx={{
              background:
                "linear-gradient(284.97deg, #018374 1.26%, #004D44 99.98%)",
              borderRadius: "12px",
              p: 3,
              mb: 2.5,
              border: "1px solid #155f54",
              boxShadow: "inset 0px 0px 10.9px rgba(119, 172, 255, 0.2)",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.625 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "white",
                  lineHeight: "28px",
                }}
              >
                Complete a Benefits Check
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  color: "#e1e5e5",
                  lineHeight: "normal",
                }}
              >
                {budgetData.estimatedBenefits ? (
                  <>
                    Based on the information you have provided, we estimate that
                    you could be entitled to an extra{" "}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {formatCurrency(budgetData.estimatedBenefits)}
                    </Box>
                    . Use our tool to find out exactly how much more in benefits
                    you could claim.
                  </>
                ) : (
                  <>
                    Find out if you're eligible for extra support using our
                    quick Benefits Check tool. See exactly how much help you
                    could get — it only takes a few minutes.
                  </>
                )}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleBenefitsCheck}
              sx={{
                backgroundColor: "white",
                color: "#155f54",
                fontSize: "16px",
                fontWeight: 600,
                px: 1.5,
                py: 1,
                borderRadius: "12px",
                textTransform: "none",
                width: "fit-content",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                },
              }}
            >
              Start benefits check
            </Button>
          </Box>

          {/* Download and Email Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 4,
              mb: 2,
            }}
          >
            <Button
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={handleDownload}
              sx={{
                color: "#003c42",
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                p: 0,
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Download
            </Button>
            <Button
              startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
              onClick={handleEmail}
              sx={{
                color: "#003c42",
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                p: 0,
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Email
            </Button>
          </Box>

          {/* Income & Assets Section */}
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              p: 2.125,
              mb: 2,
              backgroundColor: "white",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#005f60",
                mb: 1.5,
              }}
            >
              Income & Assets
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <SummaryRow
                label="Calculated monthly income"
                value={budgetData.calculatedMonthlyIncome}
                bold
              />
              <SummaryRow
                label="Calculated assets"
                value={budgetData.calculatedAssets}
                bold
              />
            </Box>
          </Paper>

          {/* Expenses Section */}
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              p: 2.125,
              mb: 2,
              backgroundColor: "white",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#005f60",
                mb: 1.5,
              }}
            >
              Expenses
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <SummaryRow
                label="Calculated fixed monthly expenses"
                value={budgetData.calculatedFixedMonthlyExpenses}
                bold
              />
              <SummaryRow
                label="Calculated flexible monthly expenses"
                value={budgetData.calculatedFlexibleMonthlyExpenses}
                bold
              />
          
            </Box>
          </Paper>

          {/* Debts Section */}
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              p: 2.125,
              mb: 2,
              backgroundColor: "white",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#005f60",
                mb: 1.5,
              }}
            >
              Debts
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <SummaryRow
                label="Total priority debts"
                value={budgetData.totalPriorityDebts}
                bold
              />
              <SummaryRow
                label="Monthly priority debt repayment"
                value={budgetData.monthlyPriorityDebtRepayment}
                bold
              />
              <SummaryRow
                label="Total non-priority debts"
                value={budgetData.totalNonPriorityDebts}
                bold
              />
              <SummaryRow
                label="Monthly non-priority debt repayment"
                value={budgetData.monthlyNonPriorityDebtRepayment}
                bold
              />
                  <SummaryRow
                label="Pending payment offers"
                value={budgetData.pendingPaymentOffers}
                bold
              />
            </Box>
          </Paper>

          {/* Total Leftover Section */}
          <Box
            sx={{
              backgroundColor: "#003c42",
              borderRadius: "8px",
              p: 2,
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#f6facf",
              }}
            >
              Total Leftover
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#d4e04e",
              }}
            >
              {formatCurrency(budgetData.totalLeftover)}
            </Typography>
          </Box>

          {/* View Budget Breakdown Link */}
          <Box
            onClick={handleViewBreakdown}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              mb: 3,
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#155f54",
              }}
            >
              View Budget Breakdown
            </Typography>
            <Box
              sx={{
                transform: "rotate(270deg)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 22, color: "#155f54" }} />
            </Box>
          </Box>
        </Box>

        {/* Mobile Bottom Nav */}
        <BottomTabBar />
      </Box>

      {/* Desktop View */}
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          backgroundColor: "#f9f9f9",
          overflow: "hidden",
        }}
      >
        {/* Header for Desktop */}
        <Header />

        {/* Content Area - Centered Container */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            overflow: "auto",
            backgroundColor: "#f9f9f9",
            px: 2,
            py: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "968px",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#f9f9f9",
            }}
          >
            <Box sx={{ px: 3, py: 2 }}>
              {/* Page Title */}
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: "#003c42",
                  mb: 2,
                }}
              >
                Your monthly budget: Summary
              </Typography>

              {/* Description */}
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "#757575",
                  mb: 2,
                  lineHeight: 1.5,
                }}
              >
                Below is a summary of the information that you have provided us.
                This is your budget. Please check all the details are correct,
                then click continue.
              </Typography>

              {/* Benefits Check Card */}
              <Box
                sx={{
                  background:
                    "linear-gradient(284.97deg, #018374 1.26%, #004D44 99.98%)",
                  borderRadius: "12px",
                  p: 3,
                  mb: 2.5,
                  border: "1px solid #155f54",
                  boxShadow: "inset 0px 0px 10.9px rgba(119, 172, 255, 0.2)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.625 }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "20px",
                      fontWeight: 600,
                      color: "white",
                      lineHeight: "28px",
                    }}
                  >
                    Complete a Benefits Check
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "16px",
                      color: "#e1e5e5",
                      lineHeight: "normal",
                    }}
                  >
                    {budgetData.estimatedBenefits ? (
                      <>
                        Based on the information you have provided, we estimate
                        that you could be entitled to an extra{" "}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {formatCurrency(budgetData.estimatedBenefits)}
                        </Box>
                        . Use our tool to find out exactly how much more in
                        benefits you could claim.
                      </>
                    ) : (
                      <>
                        Find out if you're eligible for extra support using our
                        quick Benefits Check tool. See exactly how much help you
                        could get — it only takes a few minutes.
                      </>
                    )}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleBenefitsCheck}
                  sx={{
                    backgroundColor: "white",
                    color: "#155f54",
                    fontSize: "16px",
                    fontWeight: 600,
                    px: 1.5,
                    py: 1,
                    borderRadius: "12px",
                    textTransform: "none",
                    width: "fit-content",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  Start benefits check
                </Button>
              </Box>

              {/* Download and Email Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 4,
                  mb: 2,
                }}
              >
                <Button
                  startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                  onClick={handleDownload}
                  sx={{
                    color: "#003c42",
                    fontSize: "14px",
                    fontWeight: 500,
                    textTransform: "none",
                    p: 0,
                    minWidth: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Download
                </Button>
                <Button
                  startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
                  onClick={handleEmail}
                  sx={{
                    color: "#003c42",
                    fontSize: "14px",
                    fontWeight: 500,
                    textTransform: "none",
                    p: 0,
                    minWidth: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Email
                </Button>
              </Box>

              {/* Income & Assets Section */}
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  p: 2.125,
                  mb: 2,
                  backgroundColor: "white",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#005f60",
                    mb: 1.5,
                  }}
                >
                  Income & Assets
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <SummaryRow
                    label="Calculated monthly income"
                    value={budgetData.calculatedMonthlyIncome}
                    bold
                  />
                  <SummaryRow
                    label="Calculated assets"
                    value={budgetData.calculatedAssets}
                    bold
                  />
                </Box>
              </Paper>

              {/* Expenses Section */}
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  p: 2.125,
                  mb: 2,
                  backgroundColor: "white",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#005f60",
                    mb: 1.5,
                  }}
                >
                  Expenses
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <SummaryRow
                    label="Calculated fixed monthly expenses"
                    value={budgetData.calculatedFixedMonthlyExpenses}
                    bold
                  />
                  <SummaryRow
                    label="Calculated flexible monthly expenses"
                    value={budgetData.calculatedFlexibleMonthlyExpenses}
                    bold
                  />
           
                </Box>
              </Paper>

              {/* Debts Section */}
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  p: 2.125,
                  mb: 2,
                  backgroundColor: "white",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#005f60",
                    mb: 1.5,
                  }}
                >
                  Debts
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <SummaryRow
                    label="Total priority debts"
                    value={budgetData.totalPriorityDebts}
                    bold
                  />
                  <SummaryRow
                    label="Monthly priority debt repayment"
                    value={budgetData.monthlyPriorityDebtRepayment}
                    bold
                  />
                  <SummaryRow
                    label="Total non-priority debts"
                    value={budgetData.totalNonPriorityDebts}
                    bold
                  />
                  <SummaryRow
                    label="Monthly non-priority debt repayment"
                    value={budgetData.monthlyNonPriorityDebtRepayment}
                    bold
                  />
                         <SummaryRow
                    label="Pending payment offers"
                    value={budgetData.pendingPaymentOffers}
                    bold
                  />
                </Box>
              </Paper>

              {/* Total Leftover Section */}
              <Box
                sx={{
                  backgroundColor: "#003c42",
                  borderRadius: "8px",
                  p: 2,
                  mb: 3,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#f6facf",
                  }}
                >
                  Total Leftover
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#d4e04e",
                  }}
                >
                  {formatCurrency(budgetData.totalLeftover)}
                </Typography>
              </Box>

              {/* View Budget Breakdown Link */}
              <Box
                onClick={handleViewBreakdown}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  mb: 3,
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#155f54",
                  }}
                >
                  View Budget Breakdown
                </Typography>
                <Box
                  sx={{
                    transform: "rotate(270deg)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ExpandMoreIcon sx={{ fontSize: 22, color: "#155f54" }} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Email Confirmation Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleEmailCancel}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isEmailSending}
      >
        <DialogTitle
          sx={{ fontSize: "20px", fontWeight: 600, color: "#003c42" }}
        >
          {isEmailSending ? "Sending Email..." : "Confirm Email"}
        </DialogTitle>
        <DialogContent>
          {isEmailSending ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 3,
                gap: 2,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#155f54" }} />
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#616161",
                  lineHeight: 1.5,
                  textAlign: "center",
                }}
              >
                Sending email to {pendingEmail}...
              </Typography>
            </Box>
          ) : (
            <Typography
              sx={{ fontSize: "16px", color: "#616161", lineHeight: 1.5 }}
            >
              Email a copy of your budget to {pendingEmail}?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleEmailCancel}
            disabled={isEmailSending}
            sx={{
              color: "#003c42",
              fontSize: "14px",
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "rgba(0, 60, 66, 0.04)",
              },
              "&:disabled": {
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEmailConfirm}
            variant="contained"
            disabled={isEmailSending}
            sx={{
              backgroundColor: "#155f54",
              color: "white",
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
              px: 2,
              "&:hover": {
                backgroundColor: "#004D44",
              },
              "&:disabled": {
                backgroundColor: "rgba(0, 0, 0, 0.12)",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MonthlyBudgetSummary;
